import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, Award, Users, Globe, TrendingUp, ArrowRight } from 'lucide-react';

const About = () => {
  // Scroll reveal animation
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);
  const values = [
    {
      icon: Target,
      title: 'Innovation',
      description: 'Constantly evolving with cutting-edge technology to deliver future-ready solutions',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Committed to delivering the highest quality services with precision and reliability',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Building lasting relationships through exceptional service and support',
      image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=400&fit=crop'
    },
    {
      icon: Globe,
      title: 'Integrity',
      description: 'Operating with transparency and ethical business practices in everything we do',
      image: 'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=600&h=400&fit=crop'
    }
  ];

  const milestones = [
    { 
      year: '1998', 
      event: 'Sanghvi Infotech Founded', 
      description: 'Started as a pioneering ICT solutions provider',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop'
    },
    { 
      year: '2008', 
      event: 'System Integration Expansion', 
      description: 'Expanded into turnkey ICT projects and smart infrastructure',
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=400&h=300&fit=crop'
    },
    { 
      year: '2012', 
      event: 'ISO Certifications Achieved', 
      description: 'Received ISO 27001, ISO 9001, and ISO 20000 certifications',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop'
    },
    { 
      year: '2016', 
      event: 'ISP License Acquired', 
      description: 'Became Class A ISP service provider with pan-India presence',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=300&fit=crop'
    },
    { 
      year: '2019', 
      event: 'Industry 4.0 Initiative', 
      description: 'Launched AI, ML, IoT, and Blockchain technology solutions',
      image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400&h=300&fit=crop'
    },
    { 
      year: '2024', 
      event: '5,000+ Customers Milestone', 
      description: 'Serving enterprises across banking, healthcare, education, and government sectors',
      image: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=400&h=300&fit=crop'
    }
  ];

  const teamImages = [
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Background Image */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&h=800&fit=crop" 
            alt="Office" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fadeIn">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">About Sanghvi Infotech</h1>
            <p className="text-lg md:text-xl text-blue-100">
              Two decades of excellence in delivering innovative ICT solutions and transforming businesses through technology
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Who We Are with Images */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className="scroll-reveal">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Who We Are</h2>
              <div className="w-24 h-1 bg-blue-600 mb-6"></div>
              
              <div className="space-y-4 text-gray-600">
                <p className="text-lg md:text-xl">
                  Established in 1998, Sanghvi InfoTech Pvt. Ltd. (SIPL) has pioneered in new-age connectivity & ICT solutions to enterprise-wide networks, integrating them into customer environments and providing on-going training, support and value-added services.
                </p>
                
                <p className="text-base md:text-lg">
                  With over two decades of experience, we have established ourselves as a trusted partner for businesses across various sectors including Banking, Education, Government, Smart Cities, and Corporate enterprises.
                </p>
                
                <p className="text-base md:text-lg">
                  We are actively taking part in the Industry 4.0 revolution by being involved in Artificial Intelligence, Machine Learning, Internet of Things (IoT) and Blockchain Technologies.
                </p>
              </div>
            </div>
            <div className="scroll-reveal grid grid-cols-2 gap-4">
              <img 
                src={teamImages[0]} 
                alt="Team Collaboration" 
                className="rounded-lg shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
              <img 
                src={teamImages[1]} 
                alt="Technology Team" 
                className="rounded-lg shadow-lg w-full h-64 object-cover mt-8 hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission with Images */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <Card className="border-none shadow-xl scroll-reveal overflow-hidden group">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop" 
                  alt="Vision" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center">
                    <Eye className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
              </div>
              <CardContent className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-base md:text-lg text-gray-600">
                  To be the most trusted and innovative ICT solutions provider in India, empowering businesses with secure, scalable, and future-ready technology infrastructure that drives digital transformation and sustainable growth.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl scroll-reveal overflow-hidden group">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=400&fit=crop" 
                  alt="Mission" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center">
                    <Target className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
              </div>
              <CardContent className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-base md:text-lg text-gray-600">
                  To deliver cutting-edge cybersecurity, system integration, and connectivity solutions that exceed client expectations. We strive to build lasting partnerships through exceptional service, continuous innovation, and unwavering commitment to quality.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values with Images */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group scroll-reveal" style={{animationDelay: `${index * 100}ms`}}>
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={value.image} 
                      alt={value.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journey Timeline with Images */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600">Key milestones in our growth story</p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {milestones.map((milestone, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group scroll-reveal" style={{animationDelay: `${index * 100}ms`}}>
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={milestone.image} 
                      alt={milestone.event} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <div className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-2xl">
                        {milestone.year}
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.event}</h3>
                    <p className="text-gray-600">{milestone.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">Why Choose Sanghvi Infotech?</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-12"></div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4 scroll-reveal">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Proven Track Record</h3>
                  <p className="text-gray-600">25+ years of excellence serving 5000+ satisfied customers across India</p>
                </div>
              </div>

              <div className="flex gap-4 scroll-reveal">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Award className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Certified Excellence</h3>
                  <p className="text-gray-600">ISO 27001, ISO 9001, SOC 2, and CMM Level 5 certified operations</p>
                </div>
              </div>

              <div className="flex gap-4 scroll-reveal">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Team</h3>
                  <p className="text-gray-600">Highly skilled professionals with deep domain expertise</p>
                </div>
              </div>

              <div className="flex gap-4 scroll-reveal">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Globe className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Pan-India Presence</h3>
                  <p className="text-gray-600">200+ PoPs ensuring reliable service delivery nationwide</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-900 to-blue-950 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Let's Build the Future Together</h2>
          <p className="text-lg md:text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Partner with us to transform your business with innovative technology solutions
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8 transition-all hover:scale-105">
              Get in Touch
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;